import SearchIcon from "@mui/icons-material/Search"
import { Box, Divider, InputAdornment, Modal, TextField } from "@mui/material"
import {
  FocusContext,
  navigateByDirection,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import _ from "lodash"
import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { ProgressSpinner } from "src/components/ProgressSpinner"
import { useSearchSliders } from "src/hooks/use-search-sliders"
import { usePageviewTracking } from "src/screens/Detail/use-pageview-tracking"

import { DGText } from "../../../../components/common/DGText"
import { Footer } from "../../../../components/common/Footer"
import { Slider, TTeaser } from "../../../../components/Sliders/Slider"
import { useHelper } from "../../../../hooks/use-helper"
import { useModalDismiss } from "../../../../hooks/use-modal-dismiss"
import useSdk, {
  ErrorLevel,
  SearchByTextQuery,
} from "../../../../hooks/use-sdk"
import { useSmoothNavigate } from "../../../../hooks/use-smooth-navigate"
import { useTransitions } from "../../../../hooks/use-transitions"
import useTranslate from "../../../../hooks/use-translate"
import { useEventBind } from "../../../../hooks/useEventBind"
import { comb, s } from "../../../../utilities/common"
import {
  Buttons,
  fontSizeFactor,
  keydown,
  maxSliderSize,
} from "../../../../utilities/constants"
import { theme, TI } from "../../../../utilities/theme"
import { useFocusHelper } from "../../../../utilities/use-focus-helper"
import { mapAbstractQueryType } from "../../../FacetDetail/FacetDetailScreen"
import { SearchScreenFocusKeys } from "../../search-focus-keys"

const Screen = ({ open }: { open: comb<boolean> }) => {
  const { ref, focusKey } = useFocusable({ isFocusBoundary: true })

  const sdk = useSdk(ErrorLevel.WithPrompt)
  const t = useTranslate()

  const [query, setQuery] = useState<string>("")
  const deferredQuery = useDeferredValue(query)
  const [data, setData] = useState<SearchByTextQuery>()
  const [spinner, setSpinner] = useState(false)
  const trackPageview = usePageviewTracking()
  const trackResults = usePageviewTracking(false)

  useEffect(() => {
    trackPageview("Search", "/search")
  }, [])

  const doSearch = useCallback(
    _.debounce((deferredQuery) => {
      setSpinner(true)
      sdk
        .searchByText({ input: deferredQuery, sliderPageSize: maxSliderSize })
        .then((res) => {
          setSpinner(false)
          // eliminate race condition
          if (queryHelper.current === deferredQuery) setData(res)
        })
    }, 500),
    [sdk],
  )

  useEffect(() => {
    setData(undefined)
    if (deferredQuery && deferredQuery.length > 2) {
      doSearch(deferredQuery)
    }
  }, [deferredQuery])

  const sliders = useSearchSliders(data, maxSliderSize)

  const { addFocus } = useFocusHelper()
  const navigate = useSmoothNavigate()

  const suggestions = useMemo(
    () =>
      JSON.parse(
        window.localStorage.getItem("searchSuggestions") ?? "[]",
      ) as string[],
    [],
  )

  const resultCount = useMemo(() => {
    if (!(data && data.searchByText)) return undefined
    return (
      data.searchByText.artists.totalResults +
      data.searchByText.albums.totalResults +
      data.searchByText.groups.totalResults +
      data.searchByText.documentaries.totalResults +
      data.searchByText.interviews.totalResults +
      data.searchByText.liveConcerts.totalResults +
      data.searchByText.partners.totalResults +
      data.searchByText.shorts.totalResults +
      data.searchByText.vodConcerts.totalResults +
      data.searchByText.editorialPlaylists.totalResults
    )
  }, [data])

  const transition = useTransitions()

  useEffect(() => {
    if (!!resultCount && resultCount > 0)
      trackResults("SearchResults", "/search?q=" + queryHelper.current)
  }, [resultCount])

  const queryHelper = useHelper(query)

  const onBlur = useCallback(() => {
    if (
      !!resultCount &&
      resultCount > 0 &&
      queryHelper.current &&
      queryHelper.current?.trim() !== ""
    ) {
      window.localStorage.setItem(
        "searchSuggestions",
        JSON.stringify(
          _.uniq([queryHelper.current, ...suggestions]).slice(0, 20),
        ),
      )
    }
  }, [resultCount, suggestions])

  //TODO TRANSLATION FOR TITLE
  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        style={{ transition }}
        className={"loading_in"}
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DGText
          tx={TI.H07tv}
          sx={{
            mt: s("60px"),
            mb: s("74px"),
            pl: s("96px"),
            pr: s("96px"),
            color: theme.palette.white.main,
          }}
        >
          {t("Search").toUpperCase()}
        </DGText>
        <SearchInput
          scrollRef={ref}
          query={comb.ine(query, setQuery)}
          open={open}
          onBlur={onBlur}
          suggestions={suggestions}
        />
        {suggestions.length ? (
          <SearchSuggestionContainer
            scrollRef={ref}
            setQuery={setQuery}
            suggestions={suggestions}
          />
        ) : null}
        {spinner && <ProgressSpinner />}
        {query && !spinner && query.length > 2 ? (
          <>
            <Divider
              sx={{
                borderColor: theme.palette.white[30],
                mt: s("58px"),
                mb: s("28px"),
                ml: s("96px"),
                mr: s("96px"),
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: s("28px"),
              }}
            >
              <DGText
                color={theme.palette.white[55]}
                tx={TI.Subheadline04tv}
                sx={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  maxWidth: s("1000px"),
                }}
              >
                {t("search__results_query", [query])}
              </DGText>
              {resultCount !== undefined && (
                <DGText color={theme.palette.white[55]} tx={TI.Subheadline04tv}>
                  {resultCount === 0
                    ? t("search__no_results_title")
                    : t("search__results_count_plural", [resultCount])}
                </DGText>
              )}
            </Box>
          </>
        ) : null}
        {sliders &&
          sliders.map((slider, sliderIndex) => (
            <Slider
              prefix={"SearchPrompt"}
              key={slider.title + slider.id}
              onPressTitle={(d?: TTeaser, teaserIndex?: number) => {
                if (!d || teaserIndex === undefined) return
                addFocus()
                navigate(
                  `detail/${d.type}/${d.id}/${sliderIndex + 1}/${
                    teaserIndex + 1
                  }/${slider.id}`,
                )
              }}
              onPressBrowse={() => {
                addFocus()
                navigate(
                  `browse/SEARCHTEXT-${mapAbstractQueryType[slider.id]}-${
                    slider.node
                  }/${query}/1/0`,
                )
              }}
              onFocus={({ top: y }, _, query) => {
                if (!(query && query.noScroll)) {
                  ref.current.style.transform =
                    "translate3d(0, " + (-y + s(300)) + "px, 0)"
                }
              }}
              title={slider.title}
              data={slider}
              dimensions={slider.style}
              margin={sliderIndex < sliders.length - 1}
            />
          ))}
        <Footer />
      </Box>
    </FocusContext.Provider>
  )
}

const SearchSuggestionContainer = ({
  scrollRef,
  suggestions,
  setQuery,
}: {
  scrollRef: RefObject<HTMLElement | null>
  suggestions: string[]
  setQuery: Dispatch<SetStateAction<string>>
}) => {
  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
    focusBoundaryDirections: ["left", "right"],
  })

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          mt: s("28px"),
          ml: s("96px"),
          mr: s("96px"),
          display: "flex",
          flexDirection: "row",
          "& > :not(:last-child)": {
            mr: s("10px"),
          },
          overflowX: "scroll",
        }}
      >
        {suggestions.map((item, i) => (
          <SearchSuggestion
            key={"suggestion" + item + i}
            yScrollRef={scrollRef}
            xScrollRef={ref}
            setQuery={setQuery}
            item={item}
          />
        ))}
      </Box>
    </FocusContext.Provider>
  )
}

const SearchSuggestion = ({
  xScrollRef,
  yScrollRef,
  item,
  setQuery,
}: {
  xScrollRef: RefObject<HTMLElement | null>
  yScrollRef: RefObject<HTMLElement | null>
  item: string
  setQuery: Dispatch<SetStateAction<string>>
}) => {
  const onPress = () => setQuery(item)
  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress: onPress,
    onFocus: ({ left: x, width }, __, query) => {
      if (!(query && query.noScroll)) {
        if (yScrollRef.current)
          yScrollRef.current.style.transform = "translate3d(0, 0, 0)"
        if (xScrollRef.current)
          xScrollRef.current.scroll({ left: x - width, behavior: "smooth" })
      }
    },
  })
  return (
    <Box
      ref={ref}
      onMouseOver={focusSelf}
      onMouseDown={onPress}
      sx={{
        maxWidth: s("250px"),
        p: s("18px 30px 20px 30px"),
        borderRadius: s("4px"),
        backgroundColor: focused
          ? theme.palette.white.main
          : theme.palette.white[30],
        boxShadow: `${s("0px 8px 16px 0px")} rgba(0, 0, 0, 0.6)`,
      }}
    >
      <DGText
        tx={TI.Subheadline07tv}
        color={theme.palette.black.main}
        sx={{
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {item}
      </DGText>
    </Box>
  )
}

const SearchInput = ({
  query,
  scrollRef,
  open,
  onBlur,
  suggestions,
}: {
  query: comb<string>
  scrollRef: RefObject<HTMLElement>
  open: comb<boolean>
  onBlur: () => void
  suggestions: string[]
}) => {
  const t = useTranslate()
  const { ref, focusSelf, focused } = useFocusable({
    onFocus: (_, __, query) => {
      //DGAPP-5462: not reproducible but we set the focus on the input optimistically after the animation has finished
      setTimeout(() => inputRef.current && inputRef.current.focus(), 700)
      if (!(query && query.noScroll) && scrollRef && scrollRef.current)
        scrollRef.current.style.transform = "translate3d(0, 1px, 0)"
    },
    onBlur: () => {
      if (inputRef.current) inputRef.current.blur()
      onBlur()
    },
    focusKey: SearchScreenFocusKeys.SearchInput,
  })
  const inputRef = useRef<HTMLInputElement>(null)

  const moveCaret = useCallback((direction: "left" | "right") => {
    const input = inputRef.current
    const currentPos = input?.selectionStart
    if (
      !(
        input === undefined ||
        input === null ||
        currentPos === undefined ||
        currentPos === null
      )
    ) {
      if (direction === "left" && currentPos > 0) {
        input.setSelectionRange(currentPos - 1, currentPos - 1)
      } else if (direction === "right" && currentPos < input.value.length) {
        input.setSelectionRange(currentPos + 1, currentPos + 1)
      }
    } else {
      return
    }
  }, [])

  useEffect(() => {
    focusSelf()
  }, [focusSelf])

  useModalDismiss(
    () => {
      onBlur()
      open.set(false)
      setFocus(SearchScreenFocusKeys.SearchButton)
    },
    focused,
    true,
    () => setFocus(SearchScreenFocusKeys.SearchInput),
  )

  // DGAPP-5478: useFocusable seems to override the arrow keys in the input field, so we need to create our own listener
  useEventBind<KeyboardEvent>(inputRef.current, keydown, (e) => {
    const direction =
      e.keyCode === 39 ? "right" : e.keyCode === 37 ? "left" : undefined
    if (!direction) return
    moveCaret(direction)
  })

  return (
    <Box
      sx={{ pl: s("96px"), pr: s("96px") }}
      ref={ref}
      onMouseDown={() => focusSelf()}
    >
      <TextField
        sx={{
          width: "100%",
          height: s("112px"),
          background: theme.palette.white[focused ? 70 : 55],
          borderRadius: s("8px"),
          flexDirection: "row",
          alignItems: "center",
        }}
        value={query.val}
        onChange={(e) => query.set(e.target.value)}
        placeholder={t("search__bar_placeholder")}
        onKeyUp={(e) => {
          if (e.keyCode === Buttons.Enter) {
            navigateByDirection("down", {})
            if (suggestions.length > 0) navigateByDirection("down", {})
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{ color: "black", paddingLeft: s("30px"), pr: s("10px") }}
            >
              <SearchIcon sx={{ fontSize: s("40px", false, fontSizeFactor) }} />
            </InputAdornment>
          ),
          style: {
            fontSize: s("40px"),
            fontWeight: 600,
            lineHeight: "52px",
            paddingTop: s("12px"),
            paddingBottom: s("12px"),
            width: "100%",
          },
        }}
        inputRef={inputRef}
      />
    </Box>
  )
}

export const SearchPrompt = ({ open }: { open: comb<boolean> }) => {
  return (
    <Modal
      id={"SearchPrompt"}
      key={"searchmodal"}
      open={open.val}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.main,
      }}
      hideBackdrop
    >
      <Box>{open.val ? <Screen open={open} /> : null}</Box>
    </Modal>
  )
}
